import React, { useState, useEffect } from 'react';
import { X, ArrowRight, Link, LogOut, Zap, RefreshCw, AtSign, Languages } from 'lucide-react';

interface EditRepasseModalProps {
    isOpen: boolean;
    onClose: () => void;
    connection: {
        id: number;
        from_chat: string;
        to_chat: string;
        from_title?: string;
        to_title?: string;
        replacement_url?: string;
        enable_link_replacement?: boolean;
        replacement_user?: string;
        enable_user_replacement?: boolean;
        enable_translation?: boolean;
        translation_language?: string;
    } | null;
    onSave: (data: {
        from_chat: string;
        to_chat: string;
        replacement_url: string;
        enable_link_replacement: boolean;
        replacement_user: string;
        enable_user_replacement: boolean;
        enable_translation: boolean;
        translation_language: string;
    }) => Promise<void>;
}

export const EditRepasseModal: React.FC<EditRepasseModalProps> = ({ isOpen, onClose, connection, onSave }) => {
    const [fromChat, setFromChat] = useState('');
    const [toChat, setToChat] = useState('');
    const [replacementURL, setReplacementURL] = useState('');
    const [enableLinkReplacement, setEnableLinkReplacement] = useState(false);
    const [replacementUser, setReplacementUser] = useState('');
    const [enableUserReplacement, setEnableUserReplacement] = useState(false);
    const [enableTranslation, setEnableTranslation] = useState(false);
    const [translationLanguage, setTranslationLanguage] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (connection) {
            setFromChat(connection.from_chat);
            setToChat(connection.to_chat);
            setReplacementURL(connection.replacement_url || '');
            setEnableLinkReplacement(connection.enable_link_replacement || false);
            setReplacementUser(connection.replacement_user || '');
            setEnableUserReplacement(connection.enable_user_replacement || false);
            setEnableTranslation(connection.enable_translation || false);
            setTranslationLanguage(connection.translation_language || '');
        }
    }, [connection]);

    if (!isOpen || !connection) return null;

    const handleSave = async () => {
        setLoading(true);
        try {
            await onSave({
                from_chat: fromChat,
                to_chat: toChat,
                replacement_url: replacementURL,
                enable_link_replacement: enableLinkReplacement,
                replacement_user: replacementUser,
                enable_user_replacement: enableUserReplacement,
                enable_translation: enableTranslation,
                translation_language: translationLanguage
            });
            onClose();
        } catch (e) {
            console.error('Erro ao salvar:', e);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.85)', display: 'flex', justifyContent: 'center', alignItems: 'center',
            zIndex: 1000, backdropFilter: 'blur(8px)'
        }}>
            <div style={{
                background: 'linear-gradient(135deg, #1a1a1a 0%, #0a0a0a 100%)',
                border: '1px solid #333',
                borderRadius: '16px',
                width: '90%',
                maxWidth: '600px',
                boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
                overflow: 'hidden'
            }}>
                {/* Header */}
                <div style={{
                    padding: '24px 32px',
                    borderBottom: '1px solid #333',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    background: 'linear-gradient(135deg, rgba(0, 229, 255, 0.1) 0%, rgba(0, 176, 255, 0.1) 100%)'
                }}>
                    <div>
                        <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#fff' }}>
                            Editar Repasse #{connection.id}
                        </h2>
                        <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#888' }}>
                            Atualize as configurações de origem e destino
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'rgba(255,255,255,0.1)',
                            border: 'none',
                            borderRadius: '8px',
                            width: '36px',
                            height: '36px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                        }}
                    >
                        <X size={18} color="#fff" />
                    </button>
                </div>

                {/* Body */}
                <div style={{ padding: '32px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
                    {/* Origem */}
                    <div>
                        <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#888', marginBottom: '8px', letterSpacing: '1px' }}>
                            ORIGEM
                        </label>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            background: 'rgba(255,255,255,0.05)',
                            border: '1px solid #333',
                            borderRadius: '10px',
                            padding: '12px 16px'
                        }}>
                            <LogOut size={16} color="#888" />
                            <input
                                type="text"
                                value={fromChat}
                                onChange={(e) => setFromChat(e.target.value)}
                                placeholder="ID do Canal ou @usuario"
                                style={{
                                    flex: 1,
                                    background: 'transparent',
                                    border: 'none',
                                    outline: 'none',
                                    color: '#fff',
                                    fontSize: '14px'
                                }}
                            />
                        </div>
                        {connection.from_title && (
                            <p style={{ margin: '6px 0 0 0', fontSize: '12px', color: '#666' }}>
                                📢 {connection.from_title}
                            </p>
                        )}
                    </div>

                    {/* Destino */}
                    <div>
                        <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#888', marginBottom: '8px', letterSpacing: '1px' }}>
                            DESTINO
                        </label>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            background: 'rgba(255,255,255,0.05)',
                            border: '1px solid #333',
                            borderRadius: '10px',
                            padding: '12px 16px'
                        }}>
                            <ArrowRight size={16} color="#888" />
                            <input
                                type="text"
                                value={toChat}
                                onChange={(e) => setToChat(e.target.value)}
                                placeholder="ID do Canal ou @usuario"
                                style={{
                                    flex: 1,
                                    background: 'transparent',
                                    border: 'none',
                                    outline: 'none',
                                    color: '#fff',
                                    fontSize: '14px'
                                }}
                            />
                        </div>
                        {connection.to_title && (
                            <p style={{ margin: '6px 0 0 0', fontSize: '12px', color: '#666' }}>
                                📢 {connection.to_title}
                            </p>
                        )}
                    </div>

                    {/* Substituição de Links */}
                    <div style={{ borderTop: '1px solid #333', paddingTop: '20px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: '#fff' }}>
                                <input
                                    type="checkbox"
                                    checked={enableLinkReplacement}
                                    onChange={(e) => setEnableLinkReplacement(e.target.checked)}
                                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                                />
                                Substituição de Links
                            </label>
                            <span style={{ fontSize: '10px', color: '#ff6a00', background: 'rgba(0, 229, 255, 0.1)', padding: '4px 8px', borderRadius: '4px' }}>
                                MONETIZAÇÃO
                            </span>
                        </div>

                        {enableLinkReplacement && (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                background: 'rgba(0, 229, 255, 0.05)',
                                border: '1px solid rgba(0, 229, 255, 0.2)',
                                borderRadius: '10px',
                                padding: '12px 16px',
                                animation: 'fadeIn 0.3s ease'
                            }}>
                                <Link size={16} color="#ff6a00" />
                                <input
                                    type="text"
                                    value={replacementURL}
                                    onChange={(e) => setReplacementURL(e.target.value)}
                                    placeholder="https://seu-link-afiliado.com/..."
                                    style={{
                                        flex: 1,
                                        background: 'transparent',
                                        border: 'none',
                                        outline: 'none',
                                        color: '#fff',
                                        fontSize: '14px'
                                    }}
                                />
                            </div>
                        )}

                    </div>

                    {/* Substituição de Usuario */}
                    <div style={{ borderTop: '1px solid #333', paddingTop: '20px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: '#fff' }}>
                                <input
                                    type="checkbox"
                                    checked={enableUserReplacement}
                                    onChange={(e) => setEnableUserReplacement(e.target.checked)}
                                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                                />
                                Substituição de @Usuario
                            </label>
                            <span style={{ fontSize: '10px', color: '#fff', background: 'rgba(255, 255, 255, 0.1)', padding: '4px 8px', borderRadius: '4px' }}>
                                MENÇÃO
                            </span>
                        </div>

                        {enableUserReplacement && (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                background: 'rgba(255, 255, 255, 0.05)',
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                borderRadius: '10px',
                                padding: '12px 16px',
                                animation: 'fadeIn 0.3s ease'
                            }}>
                                <AtSign size={16} color="#ccc" />
                                <input
                                    type="text"
                                    value={replacementUser}
                                    onChange={(e) => setReplacementUser(e.target.value)}
                                    placeholder="@NovoUsuario"
                                    style={{
                                        flex: 1,
                                        background: 'transparent',
                                        border: 'none',
                                        outline: 'none',
                                        color: '#fff',
                                        fontSize: '14px'
                                    }}
                                />
                            </div>
                        )}

                        {/* Translation Settings */}
                        <div style={{ borderTop: '1px solid #333', paddingTop: '20px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: '#fff' }}>
                                    <input
                                        type="checkbox"
                                        checked={enableTranslation}
                                        onChange={(e) => setEnableTranslation(e.target.checked)}
                                        style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                                    />
                                    Tradução Automática
                                </label>
                                <span style={{ fontSize: '10px', color: '#06b6d4', background: 'rgba(6, 182, 212, 0.1)', padding: '4px 8px', borderRadius: '4px', fontWeight: '600', letterSpacing: '0.05em' }}>IDIOMA</span>
                            </div>

                            {enableTranslation && (
                                <div className="input-wrapper fade-in">
                                    <Languages size={16} color="#aaa" />
                                    <select
                                        value={translationLanguage}
                                        onChange={(e) => setTranslationLanguage(e.target.value)}
                                    >
                                        <option value="">Selecione o idioma</option>
                                        <option value="en">🇬🇧 Inglês</option>
                                        <option value="es">🇪🇸 Espanhol</option>
                                    </select>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div style={{
                    padding: '20px 32px',
                    borderTop: '1px solid #333',
                    display: 'flex',
                    gap: '12px',
                    justifyContent: 'flex-end',
                    background: 'rgba(0,0,0,0.3)'
                }}>
                    <button
                        onClick={onClose}
                        disabled={loading}
                        style={{
                            padding: '12px 24px',
                            background: 'rgba(255,255,255,0.05)',
                            border: '1px solid #333',
                            borderRadius: '8px',
                            color: '#fff',
                            fontSize: '14px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                        }}
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={loading || !fromChat || !toChat}
                        style={{
                            padding: '12px 24px',
                            background: loading ? '#555' : 'var(--btn-gradient)',
                            border: 'none',
                            borderRadius: '8px',
                            color: '#fff',
                            fontSize: '14px',
                            fontWeight: 600,
                            cursor: loading ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            transition: 'all 0.2s',
                            opacity: (!fromChat || !toChat) ? 0.5 : 1
                        }}
                    >
                        {loading ? <RefreshCw size={16} className="spin" /> : <Zap size={16} />}
                        {loading ? 'Salvando...' : 'Salvar Alterações'}
                    </button>
                </div>
            </div>
        </div>
    );
};
